import {useCallback, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {AgencyRepository} from '../repositories/AgencyRepository.ts'
import {AuthenticationRepository} from '../repositories/AuthenticationRepository.ts'
import {GetAgencyPaymentPlansUseCase} from '../domain/useCases/agencies/GetAgencyPaymentPlansUseCase.ts'
import {CreateAgencyPaymentPlanUseCase} from '../domain/useCases/agencies/CreateAgencyPaymentPlanUseCase.ts'
import {RenegotiatePaymentPlanUseCase} from '../domain/useCases/agencies/RenegotiatePaymentPlanUseCase.ts'
import {CancelPaymentPlanUseCase} from '../domain/useCases/agencies/CancelPaymentPlanUseCase.ts'
import {CreatePaymentPlanPayload, InstallmentDraft, PaymentPlan} from '../domain/entities/PaymentPlan.ts'
import {parseBackendError} from '../utils/parseBackendError.ts'

const agencyRepo = new AgencyRepository(new AuthenticationRepository())
const getPaymentPlansUseCase = new GetAgencyPaymentPlansUseCase(agencyRepo)
const createPaymentPlanUseCase = new CreateAgencyPaymentPlanUseCase(agencyRepo)
const renegotiatePaymentPlanUseCase = new RenegotiatePaymentPlanUseCase(agencyRepo)
const cancelPaymentPlanUseCase = new CancelPaymentPlanUseCase(agencyRepo)

// Payment plans are admin-only on the backend, so they're only fetched when enabled.
export function usePaymentPlans(agencyId: string, enabled: boolean) {
    const {t} = useTranslation('dashboard')
    const [plans, setPlans] = useState<PaymentPlan[]>([])
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!agencyId || !enabled) return
        getPaymentPlansUseCase.execute(agencyId)
            .then(setPlans)
            .catch((err) => {
                console.error('Error loading payment plans:', err)
                setError(t('paymentPlans.loadError'))
            })
    }, [agencyId, enabled, t])

    // Each action rejects with a message ready to show, after logging the cause.
    const fail = useCallback((what: string, fallbackKey: string) => (err: unknown) => {
        console.error(`Error ${what}:`, err)
        throw new Error(parseBackendError(err, t(fallbackKey)).message)
    }, [t])

    const create = useCallback((payload: CreatePaymentPlanPayload) =>
        createPaymentPlanUseCase.execute(agencyId, payload)
            .then((created) => setPlans((prev) => [created, ...prev]))
            .catch(fail('creating payment plan', 'paymentPlans.saveError')), [agencyId, fail])

    const renegotiate = useCallback((planId: string, installments: InstallmentDraft[]) =>
        renegotiatePaymentPlanUseCase.execute(agencyId, planId, installments)
            .then((updated) => setPlans((prev) => prev.map((p) => p.id === updated.id ? updated : p)))
            .catch(fail('renegotiating payment plan', 'paymentPlans.saveError')), [agencyId, fail])

    const cancel = useCallback((planId: string) =>
        cancelPaymentPlanUseCase.execute(agencyId, planId)
            .then(() => setPlans((prev) => prev.filter((p) => p.id !== planId)))
            .catch(fail('cancelling payment plan', 'paymentPlans.cancelError')), [agencyId, fail])

    return {plans, error, create, renegotiate, cancel}
}
